// src/components/CariHesaplar.js

import React, { useState, useMemo } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
    Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TextField, InputAdornment, Button, Stack, Divider, useTheme, useMediaQuery,
    FormControlLabel, Switch
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PageviewIcon from '@mui/icons-material/Pageview';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import RefreshIcon from '@mui/icons-material/Refresh';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import GlassCard from '../ui/glass/components/GlassCard';
import PageHeader from '../components/common/PageHeader';
import EmptyState from '../components/common/EmptyState';
import { Loading } from '../components/common/Loading';
import { exportToExcel } from '../utils/exportToExcel';
import useCachedCollection from '../hooks/useCachedCollection';

// Cari hesaplar Excel kolon tanımı (salt-okunur export)
const cariExportColumns = [
    { header: 'Müşteri Adı', value: (c) => c.adSoyad, width: 24 },
    { header: 'Telefon', value: (c) => c.telefon || '', width: 15 },
    { header: 'Servis Sayısı', value: (c) => c.serviceRecordCount || 0, type: 'number', width: 13 },
    { header: 'Toplam Ciro (₺)', value: (c) => c.totalRevenue, type: 'money', width: 16 },
    { header: 'Toplam Tahsilat (₺)', value: (c) => c.totalPaid, type: 'money', width: 18 },
    { header: 'Güncel Bakiye (₺)', value: (c) => c.balance, type: 'money', width: 16 },
];

const CariHesaplar = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [showOnlyDebtors, setShowOnlyDebtors] = useState(false);
    const [visibleCount, setVisibleCount] = useState(25); // Faz 7: ekranda gösterilen satır sayısı
    const navigate = useNavigate();

    // YENİ: Mobil ekran kontrolü
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // Faz 7: realtime onSnapshot yerine oturum-içi cache. Her koleksiyon oturumda
    // 1 kez okunur; ekranlar arası geçişte tekrar okunmaz. "Yenile" ile güncellenir.
    // Bu ekran salt-okuma (yazma yok) olduğundan ayrıca cache tazeleme gerekmez.
    const { data: customers, loading: customersLoading, refresh: refreshCustomers } = useCachedCollection('customers');
    const { data: serviceRecords, loading: serviceLoading, refresh: refreshService } = useCachedCollection('serviceRecords');
    const { data: transactions, loading: transactionsLoading, refresh: refreshTransactions } = useCachedCollection('transactions');
    const loading = customersLoading || serviceLoading || transactionsLoading;

    const handleRefresh = () => {
        refreshCustomers();
        refreshService();
        refreshTransactions();
    };

    const customerFinancials = useMemo(() => {
        const financials = {};

        customers.forEach(customer => {
            financials[customer.id] = { ...customer, totalRevenue: 0, totalPaid: 0, balance: 0, serviceRecordCount: 0 };
        });

        serviceRecords.forEach(record => {
            if (financials[record.musteriId]) {
                financials[record.musteriId].totalRevenue += record.toplamTutar || 0;
                financials[record.musteriId].totalPaid += record.odenenTutar || 0;
                financials[record.musteriId].serviceRecordCount += 1;
            }
        });

        transactions.forEach(t => {
            if (financials[t.customerId]) {
                if (t.type === 'borc') financials[t.customerId].totalRevenue += t.amount || 0;
                else if (t.type === 'tahsilat') financials[t.customerId].totalPaid += t.amount || 0;
            }
        });

        Object.values(financials).forEach(fin => { fin.balance = fin.totalRevenue - fin.totalPaid; });

        return Object.values(financials);
    }, [customers, serviceRecords, transactions]);

    const filteredData = customerFinancials
        .filter(customer => {
            const matchesSearch = (customer.adSoyad || '').toLowerCase().includes(searchTerm.toLowerCase());
            const matchesFilter = showOnlyDebtors ? customer.balance > 0 : true;
            return matchesSearch && matchesFilter;
        })
        .sort((a, b) => b.balance - a.balance); // Default Sort: Highest Debt First

    // Faz 7: ekranda yalnızca ilk `visibleCount` satırı göster (DOM'u hafifletir).
    // Arama/filtre TÜM veri üzerinde çalışır; export da tüm filtreli veriyi indirir.
    const visibleData = filteredData.slice(0, visibleCount);

    // Excel export (salt-okunur) — aktif filtre/aramaya uyan cari hesapları indirir
    const handleExportVisible = () => {
        if (filteredData.length === 0) {
            alert('Dışa aktarılacak cari hesap bulunamadı.');
            return;
        }
        exportToExcel({ rows: filteredData, columns: cariExportColumns, sheetName: 'Cari Hesaplar', fileName: 'Cari_Hesaplar', scopeLabel: 'Goruntulenen' });
    };

    // Excel export (salt-okunur) — filtreden bağımsız tüm cari hesapları indirir
    const handleExportAll = () => {
        if (customerFinancials.length === 0) {
            alert('Dışa aktarılacak cari hesap bulunamadı.');
            return;
        }
        const sorted = [...customerFinancials].sort((a, b) => b.balance - a.balance);
        exportToExcel({ rows: sorted, columns: cariExportColumns, sheetName: 'Cari Hesaplar', fileName: 'Cari_Hesaplar', scopeLabel: 'Tumu' });
    };

    if (loading) { return <Loading message="Cari hesaplar yükleniyor..." />; }

    const formatCurrency = (amount) => (amount || 0).toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' });

    return (
        <Box sx={{ p: { xs: 1, md: 1 } }}>
            <PageHeader
                title="Cari Hesaplar"
                subtitle="Müşteri bakiye ve borç durumlarını buradan takip edebilirsiniz."
                icon={<AccountBalanceWalletIcon />}
            >
                        <FormControlLabel
                            control={<Switch checked={showOnlyDebtors} onChange={(e) => setShowOnlyDebtors(e.target.checked)} />}
                            label="Sadece Borçlular"
                        />
                        <TextField
                            size="small"
                            variant="outlined"
                            placeholder="Müşteri Ara..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon color="action" /></InputAdornment>) }}
                            sx={{ bgcolor: 'background.paper', borderRadius: 1 }}
                        />
                        <Button
                            variant="outlined"
                            startIcon={<RefreshIcon />}
                            onClick={handleRefresh}
                            disabled={loading}
                        >
                            Yenile
                        </Button>
                        <Button
                            variant="outlined"
                            color="success"
                            startIcon={<FileDownloadIcon />}
                            onClick={handleExportVisible}
                        >
                            Görüneni İndir
                        </Button>
                        <Button
                            variant="outlined"
                            color="success"
                            startIcon={<FileDownloadIcon />}
                            onClick={handleExportAll}
                        >
                            Tümünü İndir
                        </Button>
            </PageHeader>

            {isMobile ? (
                // --- YENİ: MOBİL KART GÖRÜNÜMÜ ---
                <Stack spacing={2} sx={{ p: isMobile ? 0 : 2 }}>
                    {visibleData.map(customer => (
                        <GlassCard key={customer.id} sx={{ p: 2 }} onClick={() => navigate(`/cari-hesaplar/${customer.id}`)}>
                            <Typography variant="h6" component="h2" gutterBottom>{customer.adSoyad}</Typography>
                            <Divider sx={{ mb: 1.5 }} />
                            <Stack spacing={0.5}>
                                <Typography><b>Servis Sayısı:</b> {customer.serviceRecordCount}</Typography>
                                <Typography><b>Toplam Ciro:</b> {formatCurrency(customer.totalRevenue)}</Typography>
                                <Typography color="success.main"><b>Toplam Tahsilat:</b> {formatCurrency(customer.totalPaid)}</Typography>
                                <Typography variant="body1" sx={{ fontWeight: 'bold', color: customer.balance > 0 ? 'error.main' : customer.balance < 0 ? 'info.main' : 'success.main', mt: 1 }}>
                                    Güncel Bakiye: {formatCurrency(customer.balance)}
                                    {customer.balance < 0 && <Typography variant="caption" display="block" color="info.main">(Bu müşteriye borcunuz var)</Typography>}
                                </Typography>
                            </Stack>
                            <Button fullWidth variant="contained" size="small" component={RouterLink} to={`/cari-hesaplar/${customer.id}`} startIcon={<PageviewIcon />} sx={{ mt: 2 }} onClick={(e) => e.stopPropagation()}>
                                Ekstre
                            </Button>
                        </GlassCard>
                    ))}
                    {filteredData.length === 0 && (
                        <EmptyState
                            icon={<AccountBalanceWalletIcon />}
                            message="Cari hesap bulunamadı."
                            description={searchTerm || showOnlyDebtors ? 'Arama/filtre kriterlerinizle eşleşen kayıt yok.' : undefined}
                        />
                    )}
                    {visibleCount < filteredData.length && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, py: 1 }}>
                            <Typography variant="caption" color="text.secondary">{visibleData.length} / {filteredData.length} gösteriliyor</Typography>
                            <Button variant="outlined" onClick={() => setVisibleCount(c => c + 25)}>Daha Fazla Yükle</Button>
                        </Box>
                    )}
                </Stack>
            ) : (
                // --- MEVCUT MASAÜSTÜ TABLO GÖRÜNÜMÜ ---
                <GlassCard sx={{ width: '100%', overflow: 'hidden', p: 0 }}>
                    <TableContainer>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Müşteri Adı</TableCell>
                                    <TableCell align="center">Servis Sayısı</TableCell>
                                    <TableCell align="right">Toplam Ciro</TableCell>
                                    <TableCell align="right">Toplam Tahsilat</TableCell>
                                    <TableCell align="right">Güncel Bakiye</TableCell>
                                    <TableCell align="center">İşlemler</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {visibleData.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={6} sx={{ border: 0 }}>
                                            <EmptyState
                                                icon={<AccountBalanceWalletIcon />}
                                                message="Cari hesap bulunamadı."
                                                description={searchTerm || showOnlyDebtors ? 'Arama/filtre kriterlerinizle eşleşen kayıt yok.' : undefined}
                                            />
                                        </TableCell>
                                    </TableRow>
                                )}
                                {visibleData.map(customer => (
                                    <TableRow
                                        hover
                                        key={customer.id}
                                        sx={{ cursor: 'pointer' }}
                                        onClick={() => navigate(`/cari-hesaplar/${customer.id}`)}
                                    >
                                        <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>{customer.adSoyad}</TableCell>
                                        <TableCell align="center">{customer.serviceRecordCount}</TableCell>
                                        <TableCell align="right">{formatCurrency(customer.totalRevenue)}</TableCell>
                                        <TableCell align="right" sx={{ color: 'success.main' }}>{formatCurrency(customer.totalPaid)}</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 'bold', color: customer.balance > 0 ? 'error.main' : customer.balance < 0 ? 'info.main' : 'success.main' }}>
                                            {formatCurrency(customer.balance)}
                                        </TableCell>
                                        <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                                            <Button variant="contained" size="small" component={RouterLink} to={`/cari-hesaplar/${customer.id}`} startIcon={<PageviewIcon />}>
                                                Ekstre
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    {visibleCount < filteredData.length && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, py: 2 }}>
                            <Typography variant="caption" color="text.secondary">{visibleData.length} / {filteredData.length} gösteriliyor</Typography>
                            <Button variant="outlined" onClick={() => setVisibleCount(c => c + 25)}>Daha Fazla Yükle</Button>
                        </Box>
                    )}
                </GlassCard>
            )}
        </Box>
    );
};

export default CariHesaplar;